import clamp from 'lodash/clamp';
import {RefCallback, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import EventBanner from './EventBanner';
import ViewerEnd from './ViewerEnd';
import ICO_TOTOP from '../../../public/static/viewer/ico-totop.svg';
import AspectRatioBox from '../../common/component/AspectRatioBox';
import TextButton from '../../common/component/TextButton';
import {KeyboardKeyType} from '../../common/constant';
import {Page} from '../../common/constant/page';
import VideoClip from '../../common/container/VideoClip';
import {getIsVerticalEnd, getKeyboardKeyType} from '../../common/function';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {round} from '../../common/util/number';
import MaxWidth from '../../commonSub/component/MaxWidth';
import {getIsLayeredState, sendViewerEvent} from '../common';
import WaitForFreePlusBanner from '../component/viewerEnd/WaitForFreePlusBanner';
import ViewerImage from '../component/ViewerImage';
import useAutoScroll from '../hook/useAutoScroll';
import useMoveEpisode from '../hook/useMoveEpisode';
import useSmoothKeyScroll from '../hook/useSmoothKeyScroll';
import {actions} from '../state/actions';
import {IViewerProps} from '../type';

export default function ScrollViewer({
  contentId,
  episodeId,
  images,
  initialPage = 1,
  isRunMode,
  isStopSales,
  videoClip,
  prevPage,
  onProgressChanged,
  onPageChanged,
  goToPrevEpisode,
  goToNextEpisode,
}: IViewerProps) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [
    viewerState,
    autoScrollSpeed,
    eventBanner,
    extendBanner,
    title,
    badge,
  ] = useTypedSelector(({viewer}) => [
    viewer.viewerState,
    viewer.autoScrollSpeed,
    viewer.viewerEnd?.eventBanner,
    viewer.viewerEnd?.extendBanner,
    viewer.content?.title,
    viewer.content?.badge,
  ]);
  const showViewerEnd = !isRunMode && !isStopSales;
  const showEventBanner =
    showViewerEnd && eventBanner && prevPage !== Page.Viewer;
  const totalPage =
    images.length +
    (showViewerEnd ? 1 : 0) +
    (showEventBanner ? 1 : 0) +
    (videoClip ? 1 : 0);
  const lastImageIndex =
    videoClip?.location === 'top' ? images.length : images.length - 1;

  const isFirstKeyDownRef = useRef<boolean>(true);
  const isKeyDownToPrevRef = useRef<boolean>(false);
  const isKeyDownToNextRef = useRef<boolean>(false);
  useEffect(() => {
    if (!isRunMode) return;
    const getIsUpFromTop = (keyType: KeyboardKeyType) =>
      window.scrollY === 0 && UP_KEY_CODES.includes(keyType);
    const getIsDownFromBottom = (keyType: KeyboardKeyType) =>
      getIsVerticalEnd(contentWrapperRef.current?.offsetHeight) &&
      DOWN_KEY_CODES.includes(keyType);

    function onKeyDown(e: KeyboardEvent) {
      const keyType = getKeyboardKeyType(e);
      if (isFirstKeyDownRef.current) {
        isFirstKeyDownRef.current = false;
        isKeyDownToPrevRef.current = getIsUpFromTop(keyType);
        isKeyDownToNextRef.current = getIsDownFromBottom(keyType);
      }
    }

    function onKeyUp(e: KeyboardEvent) {
      const keyType = getKeyboardKeyType(e);
      isFirstKeyDownRef.current = true;
      if (isKeyDownToPrevRef.current && getIsUpFromTop(keyType)) {
        reset();
        goToPrevEpisode();
      } else if (isKeyDownToNextRef.current && getIsDownFromBottom(keyType)) {
        reset();
        goToNextEpisode();
      }
    }

    function reset() {
      isKeyDownToPrevRef.current = false;
      isKeyDownToNextRef.current = false;
    }

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      reset();
    };
  }, [goToNextEpisode, goToPrevEpisode, isRunMode]);
  useSmoothKeyScroll();
  useAutoScroll(autoScrollSpeed);

  const isMovedToInitRef = useRef(false);
  const pagesRef = useRef<{[key: number]: HTMLDivElement}>({});
  const initialPageEl =
    pagesRef.current?.[Math.min(initialPage, totalPage) - 1];
  useEffect(() => {
    if (initialPageEl) {
      window.setTimeout(() => {
        window.scrollTo(0, initialPageEl.offsetTop);
      }, 0);
      requestAnimationFrame(() => (isMovedToInitRef.current = true));
    }
  }, [initialPageEl]);

  const initialRef = useRef(true);
  useEffect(() => {
    if (getIsLayeredState(viewerState)) return;

    const onScroll = () => {
      const isScreenEnd = getIsVerticalEnd(
        document.documentElement.scrollHeight
      );
      const y = document.documentElement.scrollTop;
      if (!isRunMode && autoScrollSpeed === 0 && (isScreenEnd || y < 10)) {
        dispatch(actions.setViewerState('default'));
      } else if (autoScrollSpeed > 0 && isScreenEnd) {
        dispatch(actions.setValue('autoScrollSpeed', 0));
      } else {
        // 회차 변경시 컨트롤러 유지를 위해 넣음. 첫 진입이라면 viewerState 를 none 으로 변경시키지 않는다.
        if (initialRef.current) {
          initialRef.current = false;
        } else {
          isMovedToInitRef.current && dispatch(actions.setViewerState('none'));
        }
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [autoScrollSpeed, dispatch, isRunMode, viewerState]);

  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const progressRatioRef = useRef(0);
  useEffect(() => {
    if (!onProgressChanged) return;

    const onScroll = () => {
      const contentWrapper = contentWrapperRef.current;
      if (!contentWrapper) return;
      // 작품홈에서는 뷰어엔드를 제외한 프로그레스를 표시해서 이미지만 높이를 기준으로 한다.
      const ratio = getScrollRatio(contentWrapper.scrollHeight);
      if (progressRatioRef.current !== ratio) {
        progressRatioRef.current = ratio;
        onProgressChanged(ratio);
      }
    };
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [dispatch, onProgressChanged]);

  const currentPageRef = useRef(initialPage);
  useEffect(() => {
    currentPageRef.current = clamp(initialPage, 1, totalPage);
  }, [initialPage, totalPage]);

  const observerRef = useRef<IntersectionObserver>();
  const visibleIndexRef = useRef(new Set<number>());

  // 화면 최상단 이미지를 현재 페이지로 한다.
  // 스크롤 뷰는 별도로 페이지 정보를 표시하지 않아 전체 페이지가 나오지 않아도 괜찮다.
  const onPageChangedRef = useRef(onPageChanged);
  useEffect(() => {
    onPageChangedRef.current = onPageChanged;
  }, [onPageChanged]);
  useEffect(() => {
    const onIntersection = (pages: IntersectionObserverEntry[]) => {
      if (!isMovedToInitRef.current) return;

      pages.forEach(page => {
        const index = Number((page.target as HTMLElement).dataset.index);

        if (page.isIntersecting) {
          visibleIndexRef.current.add(index);
          if (index === lastImageIndex) {
            sendViewerEvent({
              actionName: 'read',
              action: 'complete',
              customProps: {
                content_id: contentId,
                episode_id: episodeId,
                viewer_type: 'scroll',
              },
            });
          }
        } else {
          visibleIndexRef.current.delete(index);
        }
      });

      let topIndex = totalPage;
      for (const visibleIndex of visibleIndexRef.current) {
        topIndex = Math.min(topIndex, visibleIndex);
      }

      if (currentPageRef.current !== topIndex) {
        currentPageRef.current = topIndex;
        onPageChangedRef.current(topIndex + 1);
      }
    };

    observerRef.current = new IntersectionObserver(onIntersection, {
      rootMargin: '-1px',
    });
    Object.values(pagesRef.current).forEach(page => {
      if (observerRef.current && page) {
        observerRef.current.observe(page);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [showViewerEnd, totalPage, lastImageIndex, contentId, episodeId]);

  useMoveEpisode({
    getIsStart: () => window.scrollY < 10,
    getIsEnd: () => getIsVerticalEnd(contentWrapperRef.current?.offsetHeight),
    goToPrevEpisode,
    goToNextEpisode,
  });

  const isTopVideoClip = videoClip?.location === 'top';
  const setPageRef: RefCallback<HTMLDivElement> = ref => {
    if (ref) {
      const index = Number(ref.dataset.index);
      pagesRef.current[index] = ref;
    }
  };
  return (
    <div className="w-full h-full">
      <div ref={contentWrapperRef} className="bg-white">
        {videoClip && isTopVideoClip && (
          <div
            ref={setPageRef}
            className={cn('mx-auto pt-44', VIEWER_MAX_WIDTH_CN)}
            data-index={0}
          >
            <VideoClip video={videoClip} isLazy={false} />
          </div>
        )}
        {images.map((image, index) => (
          <div
            key={index}
            data-index={isTopVideoClip ? index + 1 : index}
            className={cn('mx-auto', VIEWER_MAX_WIDTH_CN)}
            ref={setPageRef}
          >
            <AspectRatioBox aspectRatio={image.aspectRatio}>
              <ViewerImage src={image.url} isScrollViewImg={true} />
            </AspectRatioBox>
          </div>
        ))}
        {videoClip && videoClip.location === 'bottom' && (
          <div
            ref={setPageRef}
            data-index={images.length}
            className={cn('mx-auto', VIEWER_MAX_WIDTH_CN)}
          >
            <VideoClip video={videoClip} isLazy={false} />
          </div>
        )}
      </div>
      {!isRunMode && (
        <TextButton
          textClassName="s14-bold-black"
          className="w-full h-56 border-solid rounded-none border-y btn-white border-black/8"
          onClick={() => {
            sendViewerEvent({
              actionName: 'viewer_top',
              action: 'click',
              customProps: {
                event_layer: 'viewer_end',
                viewer_type: 'scroll',
              },
            });
            window.scrollTo(0, 0);
          }}
        >
          <img src={ICO_TOTOP} alt="" width="15" height="9" className="mr-5" />
          {t('viewer_top_button')}
        </TextButton>
      )}
      {showEventBanner && eventBanner && (
        <div className="bg-background-02 pt-20">
          <MaxWidth hasMdWidth className="px-11">
            <EventBanner
              contentId={contentId}
              episodeId={episodeId}
              banner={eventBanner}
            />
          </MaxWidth>
        </div>
      )}
      {showViewerEnd && (
        <div
          className="relative pb-[102px] overflow-hidden bg-background-02"
          data-index={videoClip ? images.length + 1 : images.length}
          ref={setPageRef}
        >
          {title && badge && (
            <WaitForFreePlusBanner
              title={title}
              badge={badge}
              hasBottomLine={!extendBanner || extendBanner?.isMoment}
            />
          )}
          <ViewerEnd goToNextEpisode={goToNextEpisode} />
        </div>
      )}
    </div>
  );
}

const VIEWER_MAX_WIDTH_CN = 'max-w-default-max-width lg-max:max-w-[490px]';

const UP_KEY_CODES = [KeyboardKeyType.ArrowUp, KeyboardKeyType.PageUp];
const DOWN_KEY_CODES = [KeyboardKeyType.ArrowDown, KeyboardKeyType.PageDown];

function getScrollRatio(scrollHeight: number) {
  return round(
    Math.min((window.scrollY + window.innerHeight) / scrollHeight, 1),
    0.1
  );
}
